// Invite links + guest waiting room for a live session (moved out of the old share bar so it keeps polling while the
// Participants panel is closed, and the call can nudge the host when someone is waiting).
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { getSessionShare, setSessionGuestLink, listGuestRequests, decideGuestRequest } from '@/api/morningMeeting';

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x?.msg || (typeof x === 'string' ? x : '')).filter(Boolean).join(', ') || fallback;
  return typeof d === 'string' ? d : fallback;
};

// Soft two-note chime when a guest starts waiting (synthesized, no asset).
let audioCtx;
function chime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    audioCtx = audioCtx || new Ctx();
    if (audioCtx.state === 'suspended') audioCtx.resume();
    const t0 = audioCtx.currentTime;
    [880, 1174.66].forEach((freq, k) => {
      const o = audioCtx.createOscillator();
      const g = audioCtx.createGain();
      o.type = 'sine';
      o.frequency.value = freq;
      o.connect(g); g.connect(audioCtx.destination);
      const t = t0 + k * 0.16;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.16, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.24);
      o.start(t); o.stop(t + 0.26);
    });
  } catch { /* audio blocked */ }
}

export function useMeetingShare(sessionId, canAdmit) {
  const [guestEnabled, setGuestEnabled] = useState(false);
  const [guestToken, setGuestToken] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [requests, setRequests] = useState([]);
  const [deciding, setDeciding] = useState('');
  const seen = useRef(new Set());
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  useEffect(() => {
    if (!sessionId) return undefined;
    let alive = true;
    getSessionShare(sessionId).then((s) => {
      if (!alive) return;
      setGuestEnabled(!!s.guest_enabled);
      setGuestToken(s.guest_token || null);
      setExpiresAt(s.guest_expires_at || null);
    }).catch(() => {});
    return () => { alive = false; };
  }, [sessionId, canAdmit]);  // re-read when someone becomes host: the guest link may have been turned on since

  // Guest admission is time-critical: a fast 4 s poll while the link is on, skipped while the tab is hidden.
  useEffect(() => {
    if (!sessionId || !guestEnabled || !canAdmit) { setRequests([]); seen.current = new Set(); return undefined; }
    let alive = true;
    const tick = (poll = false) => listGuestRequests(sessionId, poll ? { pollTag: 'meeting-lobby' } : {}).then((r) => {
      if (!alive) return;
      const reqs = r.requests || [];
      if (reqs.some((x) => !seen.current.has(x.request_id))) chime();
      seen.current = new Set(reqs.map((x) => x.request_id));
      setRequests(reqs);
    }).catch(() => {});
    tick();
    const id = setInterval(() => { if (document.visibilityState === 'visible') tick(true); }, 4000);
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive = false; clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [sessionId, guestEnabled, canAdmit]);

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success('Link copied.'); return true; } catch { toast.error('Could not copy. Copy it manually.'); return false; }
  };

  const toggleGuest = async (next) => {
    setBusy(true);
    try {
      const r = await setSessionGuestLink(sessionId, next);
      setGuestEnabled(!!r.guest_enabled);
      setGuestToken(r.guest_token || null);
      setExpiresAt(r.guest_expires_at || null);
      toast.success(next ? 'Guest link is on for 3 hours.' : 'Guest link turned off.');
    } catch (e) { toast.error(errMsg(e, 'Could not update the guest link.')); } finally { setBusy(false); }
  };

  const decide = async (requestId, action) => {
    setDeciding(requestId);
    try {
      await decideGuestRequest(sessionId, requestId, action);
      setRequests((rs) => rs.filter((r) => r.request_id !== requestId));
      toast.success(action === 'admit' ? 'Guest admitted.' : 'Guest declined.');
    } catch (e) { toast.error(errMsg(e, 'Could not update the guest.')); } finally { setDeciding(''); }
  };

  return { sessionId, canAdmit, guestEnabled, expiresAt, busy, requests, deciding, toggleGuest, decide, copy,
    teamUrl: `${origin}/relay?tab=morning-meeting&join=${sessionId}`, guestUrl: guestToken ? `${origin}/meet/guest/${guestToken}` : '' };
}
